import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import * as Sentry from '@sentry/react';
import { initGA, logPageView } from './tracking/ga';
import { initTikTok, trackPageView } from './tracking/tiktok';
import { initPostHog, capture } from './tracking/posthog';
import Login from './components/Login';
import EmailVerification from './components/EmailVerification';
import DevToolbar from './components/DevToolbar';
import Studio from './Studio';
import Homepage from './Homepage';
import Waitlist from './Waitlist';
import Impressum from './Impressum';
import Datenschutz from './Datenschutz';
import AGB from './AGB';
import CookieBanner from './components/CookieBanner';
import LinkedInCallback from './components/LinkedInCallback';
import AuthAction from './AuthAction';
import OnboardingWizard from './onboarding/OnboardingWizard';
import OnboardingAppTour from './onboarding/OnboardingAppTour';
import { useRolle } from './hooks/useRolle';
import { loginSync } from './api';
import { initVisited } from './hooks/useFirstVisit';
import { ConfirmProvider } from './components/ConfirmDialog';
import KaufErfolg from './components/KaufErfolg';
import { JobsProvider } from './contexts/JobsContext';
import MobileAppTeaser from './MobileAppTeaser';
import './App.css';

const isMobile = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Dark-Mode beim Start wiederherstellen
if (localStorage.getItem('dark_mode') === 'true') {
  document.documentElement.setAttribute('data-theme', 'dark');
}

function LoadingScreen() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: 'var(--bg-base)',
      color: 'var(--txt-muted)',
      fontFamily: 'var(--font-body)',
      fontSize: 14,
    }}>
      Wird geladen…
    </div>
  );
}

function HomepageRoute({ nutzer }) {
  if (nutzer === undefined) return <LoadingScreen />;
  if (nutzer) return <Navigate to="/dashboard" replace />;
  return <Homepage />;
}

function LoginRoute({ nutzer }) {
  if (nutzer === undefined) return <LoadingScreen />;
  if (nutzer) return <Navigate to="/dashboard" replace />;
  if (isMobile()) return <MobileAppTeaser />;
  return <><Login /><DevToolbar /></>;
}

function OnboardingRoute({ nutzer }) {
  if (nutzer === undefined) return <LoadingScreen />;
  if (isMobile()) return <MobileAppTeaser />;
  if (localStorage.getItem('onboarding_done')) return <Navigate to="/dashboard" replace />;
  return <OnboardingWizard />;
}

function ProtectedApp({ nutzer }) {
  if (nutzer === undefined) return <LoadingScreen />;

  if (!nutzer) return <><Login /><DevToolbar /></>;

  const brauchtVerifizierung =
    !nutzer.emailVerified &&
    nutzer.providerData?.[0]?.providerId === 'password';

  if (brauchtVerifizierung) return <><EmailVerification nutzer={nutzer} /><DevToolbar /></>;

  if (!localStorage.getItem('onboarding_done')) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <>
      <OnboardingAppTour />
      <Studio nutzer={nutzer} />
      <DevToolbar />
    </>
  );
}

function AdminRoute({ nutzer }) {
  const { rolle, laden } = useRolle();

  if (nutzer === undefined || laden) return <LoadingScreen />;
  if (!nutzer) return <Navigate to="/" replace />;
  if (rolle !== 'admin') return <Navigate to="/dashboard" replace />;

  return <ProtectedApp nutzer={nutzer} adminView={true} />;
}

function AppRoutes() {
  const [nutzer, setNutzer] = useState(undefined);

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      setNutzer(user);
      if (user) {
        Sentry.setUser({ id: user.uid });
        loginSync().then(r => initVisited(r.data.visited_pages)).catch(() => initVisited([]));
        // signup_completed für Google/OAuth — nur bei Neu-Anlage
        const isNew = user.metadata.creationTime === user.metadata.lastSignInTime;
        if (isNew) {
          const method = user.providerData?.[0]?.providerId === 'google.com' ? 'google' : 'oauth';
          capture('signup_completed', { method });
        }
      } else {
        Sentry.setUser(null);
      }
    });
  }, []);

  return (
    <Routes>
      {/* Öffentliche Seiten */}
      <Route path="/" element={<HomepageRoute nutzer={nutzer} />} />
      <Route path="/linkedin-callback" element={<LinkedInCallback />} />
      <Route path="/auth/action" element={<AuthAction />} />
      <Route path="/login" element={<LoginRoute nutzer={nutzer} />} />
      <Route path="/waitlist" element={<Waitlist />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/agb" element={<AGB />} />

      {/* Onboarding */}
      <Route path="/onboarding" element={<OnboardingRoute nutzer={nutzer} />} />

      {/* Geschützte App-Routen */}
      <Route path="/dashboard" element={<ProtectedApp nutzer={nutzer} />} />
      <Route path="/analytics" element={<ProtectedApp nutzer={nutzer} />} />
      <Route path="/suche" element={<ProtectedApp nutzer={nutzer} />} />
      <Route path="/stelle/:id" element={<ProtectedApp nutzer={nutzer} />} />
      <Route path="/profileinstellungen" element={<ProtectedApp nutzer={nutzer} />} />
      <Route path="/meine-dokumente" element={<ProtectedApp nutzer={nutzer} />} />
      <Route path="/kauf/erfolg" element={<KaufErfolg />} />
      <Route path="/kauf/abbruch" element={<ProtectedApp nutzer={nutzer} />} />

      {/* Admin */}
      <Route path="/admin" element={<AdminRoute nutzer={nutzer} />} />
      <Route path="/admin/*" element={<AdminRoute nutzer={nutzer} />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Trackt Page-Views bei SPA-Navigation (nur wenn Consent erteilt) */
function TrackingInit() {
  const location = useLocation();

  useEffect(() => {
    // Einmalig beim Laden: Tracker initialisieren (reagieren auf Consent-State)
    initGA();
    initTikTok();
    initPostHog();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Bei jedem Routenwechsel Page-View feuern
    logPageView(location.pathname);
    trackPageView();
    // Funnel-Events für wichtige Pfade
    const pageEvents = {
      '/':          'landing_page_viewed',
      '/login':     'auth_panel_viewed',
      '/dashboard': 'dashboard_viewed',
    };
    const event = pageEvents[location.pathname];
    if (event) capture(event);
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ConfirmProvider>
        <JobsProvider>
          <TrackingInit />
          <AppRoutes />
          <CookieBanner />
        </JobsProvider>
      </ConfirmProvider>
    </BrowserRouter>
  );
}

export default App;
